import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, AlertCircle } from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';

const CompletenessRuleForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        fieldName: '',
        fieldLabel: '',
        clientType: 'RETAIL',
        required: true,
        weight: 1,
        companyId: '',
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isEditMode) {
            fetchRuleDetails();
        }
    }, []);

    const fetchRuleDetails = async () => {
        try {
            setLoading(true);
            // We need to fetch all rules and find this one since there's no getById endpoint
            // Try both client types
            for (const type of ['RETAIL', 'CORPORATE']) {
                const response = await dataQualityService.rules.getAll(type);
                const rules = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
                const rule = rules.find(r => r.id === Number(id));
                if (rule) {
                    setFormData({
                        fieldName: rule.fieldName || '',
                        fieldLabel: rule.fieldLabel || '',
                        clientType: rule.clientType || 'RETAIL',
                        required: rule.required ?? true,
                        weight: rule.weight || 1,
                        companyId: rule.company?.id || '',
                    });
                    break;
                }
            }
        } catch (error) {
            console.error('Error fetching rule:', error);
            setError('Failed to load rule details');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        try {
            const payload = {
                fieldName: formData.fieldName,
                fieldLabel: formData.fieldLabel,
                clientType: formData.clientType,
                required: formData.required,
                weight: Number(formData.weight),
                ...(formData.companyId ? { companyId: Number(formData.companyId) } : {}),
            };

            let response;
            if (isEditMode) {
                response = await dataQualityService.rules.update(id, payload);
            } else {
                response = await dataQualityService.rules.create(payload);
            }

            if (response.success) {
                navigate('/data-quality/rules');
            } else {
                setError(response.message || 'Failed to save rule');
            }
        } catch (error) {
            console.error('Error saving rule:', error);
            setError(error.message || 'Failed to save rule');
        } finally {
            setSaving(false);
        }
    };

    const clientTypeOptions = [
        { value: 'RETAIL', label: 'Retail' },
        { value: 'CORPORATE', label: 'Corporate' },
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/data-quality/rules')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-2xl font-semibold text-gray-900">
                        {isEditMode ? 'Edit Completeness Rule' : 'Add Completeness Rule'}
                    </h2>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Rule Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Field Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Field Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.fieldName}
                                    onChange={(e) => setFormData(prev => ({ ...prev, fieldName: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., phoneNumber"
                                    required
                                />
                            </div>

                            {/* Field Label */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Field Label <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.fieldLabel}
                                    onChange={(e) => setFormData(prev => ({ ...prev, fieldLabel: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Phone Number"
                                    required
                                />
                            </div>

                            {/* Client Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Client Type <span className="text-red-500">*</span>
                                </label>
                                <CustomSelect
                                    value={formData.clientType}
                                    onChange={(value) => setFormData(prev => ({ ...prev, clientType: value }))}
                                    options={clientTypeOptions}
                                    placeholder="Select client type"
                                />
                            </div>

                            {/* Weight */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Weight
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={formData.weight}
                                    onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Company ID */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company ID (optional)
                                </label>
                                <input
                                    type="number"
                                    value={formData.companyId}
                                    onChange={(e) => setFormData(prev => ({ ...prev, companyId: e.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Leave empty for global rule"
                                />
                            </div>
                        </div>

                        {/* Required */}
                        <div className="mt-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.required}
                                    onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Required Field</span>
                            </label>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex items-center gap-3 pt-6 border-t">
                        <button
                            type="submit"
                            disabled={saving || !formData.fieldName || !formData.fieldLabel || !formData.clientType}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {isEditMode ? 'Update Rule' : 'Create Rule'}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/data-quality/rules')}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CompletenessRuleForm;
